import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WorkItemBulkOperationType,
  WorkItemState,
} from '@robocorp/robocloud-orchestrator-interface';
import { WorkItem } from '@robocorp/robocloud-search-iternal-sdk';
import { FilterGroup } from '@robocorp/components';
import { CurrentWorkspaceRestriction } from '~/components';
import { useParams } from '~/hooks';
import {
  WorkItemSearchArgs,
  useStartProcessRunWithItemsMutation,
  useTriggerWorkItemBulkOperationMutation,
} from '~/queries';
import { SelectFilter as SelectFilterComponent } from '~/components/filters';
import { WorkItemStateShort } from '~/types/resources';
import {
  BulkDeleteAction,
  BulkDiscardAction,
  BulkRetryAction,
  BulkStartRunAction,
} from './BulkAction';

type SelectedItemStates = {
  new: string[];
  failed: string[];
  notInProgress: string[];
};
const defaultWorkItemStates: SelectedItemStates = { new: [], failed: [], notInProgress: [] };

type SelectedBulkItemStates = Omit<Record<keyof SelectedItemStates, number>, 'new'>;
const defaultBulkWorkItemStates: SelectedBulkItemStates = { failed: 0, notInProgress: 0 };

const getPageWorkItemStates = (workItems: WorkItem[], selected: number[]): SelectedItemStates =>
  (workItems.length > 0 ? selected : []).reduce<SelectedItemStates>(
    (acc, idx) => {
      const workItem = workItems[idx];

      // work item can disapear when switching pages or updating filters
      if (!workItem) return acc;
      if (workItem.state === WorkItemState.New) acc.new.push(workItem.id);
      if (workItem.state === WorkItemState.Failed) acc.failed.push(workItem.id);
      if (workItem.state !== WorkItemState.InProgress) acc.notInProgress.push(workItem.id);
      return acc;
    },
    { new: [], failed: [], notInProgress: [] },
  );

const getBulkWorkItemStates = (workItemStates: WorkItemStateShort[]): SelectedBulkItemStates => ({
  failed:
    workItemStates.length === 0 ||
    workItemStates.some((state) => state === WorkItemStateShort.Failed)
      ? Infinity
      : 0,
  notInProgress:
    workItemStates.length === 0 ||
    workItemStates.some((state) => state !== WorkItemStateShort.InProgress)
      ? Infinity
      : 0,
});

type Props = {
  processId: string;
  workItems: WorkItem[];
  selected: number[];
  total: number;
  onSelectChange: (select: number[]) => void;
  filters: WorkItemSearchArgs;
  filterOptions: Record<string, FilterGroup>;
};

const Content: FC<Props> = ({
  processId,
  workItems,
  selected,
  total,
  onSelectChange,
  filters,
  filterOptions,
}) => {
  const navigate = useNavigate();

  const { organizationShortName, workspaceShortName } = useParams<
    'organizationShortName' | 'workspaceShortName'
  >();

  const onSelectAll = useCallback(
    () => onSelectChange(new Array(total).fill(0, 0, workItems.length).map((_, index) => index)),
    [total, workItems.length, onSelectChange],
  );

  const { mutateAsync: startProcessRunWithItemsAsync, isLoading: isStartProcessRunLoading } =
    useStartProcessRunWithItemsMutation();
  const { mutateAsync: retryWorkItemAsync, isLoading: isRetryLoading } =
    useTriggerWorkItemBulkOperationMutation();
  const { mutateAsync: discardWorkItemAsync, isLoading: isDiscardLoading } =
    useTriggerWorkItemBulkOperationMutation();
  const { mutateAsync: deleteWorkItemsAsync, isLoading: isDeleteLoading } =
    useTriggerWorkItemBulkOperationMutation();

  const isBulkOperation = useMemo(
    () => selected.length > 0 && total === selected.length && workItems.length < total,
    [total, selected.length, workItems.length],
  );
  const selectedWorkItemStates = useMemo(
    () => (!isBulkOperation ? getPageWorkItemStates(workItems, selected) : defaultWorkItemStates),
    [workItems, selected, isBulkOperation],
  );

  const activeFilters = isBulkOperation ? filters : undefined;
  const filterState = filters?.filters?.state ?? [];
  const selectedBulkWorkItemStates = useMemo(
    () => (isBulkOperation ? getBulkWorkItemStates(filterState) : defaultBulkWorkItemStates),
    [filterState, total, isBulkOperation],
  );

  const onRunWithWorkItems = useCallback(
    () =>
      startProcessRunWithItemsAsync({ processId, workItemIds: selectedWorkItemStates.new })
        .then((data) => {
          if (data) {
            void navigate(
              `/${organizationShortName}/${workspaceShortName}/processes/${processId}/runs/${data.id}`,
            );
          }
        })
        .catch(() => onSelectChange([])),
    [
      navigate,
      onSelectChange,
      organizationShortName,
      processId,
      startProcessRunWithItemsAsync,
      selectedWorkItemStates.new,
      workspaceShortName,
    ],
  );

  const onRetryWorkItems = () =>
    retryWorkItemAsync({
      processId,
      operation: WorkItemBulkOperationType.Retry,
      ...(isBulkOperation ? { filters } : { workItemIds: selectedWorkItemStates.failed }),
    }).then(() => onSelectChange([]));

  const onDiscardWorkItems = () =>
    discardWorkItemAsync({
      processId,
      operation: WorkItemBulkOperationType.MarkAsDone,
      ...(isBulkOperation ? { filters } : { workItemIds: selectedWorkItemStates.failed }),
    }).then(() => onSelectChange([]));

  const onDeleteWorkItems = () =>
    deleteWorkItemsAsync({
      processId,
      operation: WorkItemBulkOperationType.Delete,
      ...(isBulkOperation ? { filters } : { workItemIds: selectedWorkItemStates.notInProgress }),
    }).then(() => onSelectChange([]));

  const isLoading =
    isStartProcessRunLoading || isRetryLoading || isDiscardLoading || isDeleteLoading;

  return (
    <SelectFilterComponent
      selected={selected.length}
      onDeselect={() => onSelectChange([])}
      total={total}
      onSelectAll={onSelectAll}
    >
      <CurrentWorkspaceRestriction permission="trigger_processes">
        {!isBulkOperation && (
          <BulkStartRunAction
            onConfirm={onRunWithWorkItems}
            itemCount={selectedWorkItemStates.new.length}
            loading={isStartProcessRunLoading}
            disabled={isLoading}
          />
        )}

        <BulkRetryAction
          onConfirm={onRetryWorkItems}
          activeFilters={activeFilters}
          filterOptions={filterOptions}
          itemCount={Math.max(
            selectedBulkWorkItemStates.failed,
            selectedWorkItemStates.failed.length,
          )}
          loading={isRetryLoading}
          disabled={isLoading}
        />

        <BulkDiscardAction
          onConfirm={onDiscardWorkItems}
          activeFilters={activeFilters}
          filterOptions={filterOptions}
          itemCount={Math.max(
            selectedBulkWorkItemStates.failed,
            selectedWorkItemStates.failed.length,
          )}
          loading={isDiscardLoading}
          disabled={isLoading}
        />

        <BulkDeleteAction
          onConfirm={onDeleteWorkItems}
          activeFilters={activeFilters}
          filterOptions={filterOptions}
          itemCount={Math.max(
            selectedBulkWorkItemStates.notInProgress,
            selectedWorkItemStates.notInProgress.length,
          )}
          loading={isDeleteLoading}
          disabled={isLoading}
        />
      </CurrentWorkspaceRestriction>
    </SelectFilterComponent>
  );
};

export const SelectFilter = Content;
